import toArray from 'src/app/utils/array';
import { JobsQueryRow, JobsQueryRowField } from '../models/jobs-query-row';
import { TableSchema } from '../models/table-schema';
import { TableSchemaField } from '../models/table-schema-field';

/**
 * Merge a rowset returned from the API with a table schema.
 *
 * @private
 *
 * @param {object} schema
 * @param {array} rows
 * @param {object} options
 * @param {boolean|IntegerTypeCastOptions} options.wrapIntegers Wrap values of
 *     'INT64' type in {@link BigQueryInt} objects.
 *     If a `boolean`, this will wrap values in {@link BigQueryInt} objects.
 *     If an `object`, this will return a value returned by
 *     `wrapIntegers.integerTypeCastFunction`.
 *     Please see {@link IntegerTypeCastOptions} for options descriptions.
 * @param {array} options.selectedFields List of fields to return.
 * If unspecified, all fields are returned.
 * @param {array} options.parseJSON parse a 'JSON' field into a JSON object.
 * @returns Fields using their matching names from the table's schema.
 */
export function mergeSchemaWithRows_(
    schema: TableSchema | TableSchemaField,
    rows: JobsQueryRow[],
    options: {
        wrapIntegers: boolean; // | IntegerTypeCastOptions;
        selectedFields?: string[];
        parseJSON?: boolean;
    }
) {
    if (options.selectedFields && options.selectedFields!.length > 0) {
        const selectedFieldsArray = options.selectedFields!.map((c) => {
            return c.split('.');
        });

        const currentFields = selectedFieldsArray.map((c) => c.shift());
        //filter schema fields based on selected fields.
        schema.fields = schema.fields?.filter(
            (field) =>
                currentFields
                    .map((c) => c!.toLowerCase())
                    .indexOf(field.name!.toLowerCase()) >= 0
        );
        options.selectedFields = selectedFieldsArray
            .filter((c) => c.length > 0)
            .map((c) => c.join('.'));
    }

    return toArray(rows).map(mergeSchema).map(flattenRows);

    function mergeSchema(row: JobsQueryRow) {
        return row.f!.map((field: JobsQueryRowField, index: number) => {
            const schemaField = schema.fields![index];
            let value = field.v;
            if (schemaField.mode === 'REPEATED') {
                value = (value as JobsQueryRowField[]).map((val) => {
                    return convert(schemaField, val.v, options);
                });
            } else {
                value = convert(schemaField, value, options);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fieldObject: any = {};
            fieldObject[schemaField.name!] = value;
            return fieldObject;
        });
    }

    function convert(
        schemaField: TableSchemaField,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any,
        options: {
            wrapIntegers: boolean; // | IntegerTypeCastOptions;
            selectedFields?: string[];
            parseJSON?: boolean;
        }
    ) {
        if (value === null) {
            return value;
        }

        switch (schemaField.type) {
            case 'BOOLEAN':
            case 'BOOL': {
                value = value.toLowerCase() === 'true';
                break;
            }
            case 'BYTES': {
                value = Buffer.from(value, 'base64');
                break;
            }
            case 'FLOAT':
            case 'FLOAT64': {
                value = Number(value);
                break;
            }
            case 'INTEGER':
            case 'INT64': {
                // TODO
                //   const {wrapIntegers} = options;
                //   value = wrapIntegers
                //           ? typeof wrapIntegers === 'object'
                //       ? BigQuery.int(
                //           {integerValue: value, schemaFieldName: schemaField.name},
                //           wrapIntegers
                //         ).valueOf()
                //       : BigQuery.int(value)
                //           : Number(value);
                value = Number(value);
                break;
            }
            case 'NUMERIC': {
                //   value = new Big(value);
                value = Number(value);
                break;
            }
            case 'BIGNUMERIC': {
                //   value = new Big(value);
                value = Number(value);
                break;
            }
            case 'RECORD': {
                value = mergeSchemaWithRows_(
                    schemaField,
                    value,
                    options
                ).pop();
                break;
            }
            case 'DATE': {
                // value = BigQuery.date(value);
                value = { value: String(value) };
                break;
            }
            case 'DATETIME': {
                // value = BigQuery.datetime(value);
                value = { value: String(value) };
                break;
            }
            case 'TIME': {
                // value = BigQuery.time(value);
                value = String(value);
                break;
            }
            case 'TIMESTAMP': {
                // value = BigQuery.timestamp(value);
                value = String(value);
                break;
            }
            case 'GEOGRAPHY': {
                // value = BigQuery.geography(value);
                value = String(value);
                break;
            }
            case 'JSON': {
                const { parseJSON } = options;
                value = parseJSON ? JSON.parse(value) : value;
                break;
            }
            default:
                break;
        }

        return value;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function flattenRows(rows: any[]) {
        return rows.reduce((acc, row) => {
            const key = Object.keys(row)[0];
            acc[key] = row[key];
            return acc;
        }, {});
    }

    // function date(value: BigQueryDateOptions | string) {
    //     return new BigQueryDate(value);
    //   }
}
